/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, {
    FunctionComponent,
    useContext,
    useEffect,
    useState,
} from 'react';
import styled from 'styled-components';

import { MarketCode } from '@toucan/config/markets';

import { ProductCategory } from '@toucan/types/interfaces';

import { getFilteredBrandsByShortList } from '@toucan/helpers/brands';
import { getDefaultFooterLinks } from '@toucan/helpers/footer-links';
import { normalizeIsoCode } from '@toucan/helpers/language';
import {
    CategoryNavigationType,
    getTaxonomyNavigation,
} from '@toucan/helpers/navigation';

import { Footer, FooterVariant } from '@toucan/components/organisms/footer';
import { LoginForm } from '@toucan/components/organisms/login-form';
import { CategoryList } from '@toucan/components/organisms/mobile-main-nav/components/category-list';

import { SocialChannels } from '@toucan/components/molecules/social-channels';

import AccountIcon from '@toucan/icons/user.svg';

import { OriginalContentNavItem } from '@toucan/client/core/contentful/config/types';
import { getNavOriginalContent } from '@toucan/client/core/contentful/requests';
import {
    ApplicationContext,
    ModalSize,
    TaxonomyContext,
} from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { UserContext } from '@toucan/client/global/user/context';

import { styles } from './mobile-main-nav.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface MobileMainNavProps {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

const StyledMobileMainNav = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const MobileMainNav: FunctionComponent<MobileMainNavProps> = ({
    ...otherProps
}: MobileMainNavProps) => {
    const { t } = useTranslation();
    const router = useRouter();
    const { activeMarket } = useContext(MarketContext);
    const { categories: categoriesTaxonomy, brands: brandsTaxonomy } =
        useContext(TaxonomyContext);
    const {
        setModalVisible,
        setModalContent,
        setModalSize,
        setMenuVisible,
        legalLinks,
    } = useContext(ApplicationContext);
    const { isLoggedIn } = useContext(UserContext);

    const [originalContentLinks, setOriginalContentLinks] = useState<
        OriginalContentNavItem[]
    >([]);

    const footerLinks = getDefaultFooterLinks(t, activeMarket, legalLinks);

    const categories = getTaxonomyNavigation(
        categoriesTaxonomy,
        activeMarket,
        t,
        CategoryNavigationType.CATEGORY
    );

    const brands = getTaxonomyNavigation(
        getFilteredBrandsByShortList(brandsTaxonomy),
        activeMarket,
        t,
        CategoryNavigationType.BRAND
    );

    /**
     * Map and transform ProductCategories for use in the mobile menu
     *
     * @param {ProductCategory} categories - The categories to map
     * @returns {ProductCategory} - The categories
     */
    function mapCategoriesForMobileMenu(
        categories: ProductCategory[]
    ): ProductCategory {
        return {
            id: null,
            title: t('Shop'),
            url: '',
            templatePath: '',
            categories,
        };
    }

    const searchSlug = t('search', { ns: 'urls' });
    const brandsSlug = t('brands', { ns: 'urls' });

    const brandsLinkProps =
        activeMarket.code === MarketCode.FR_FR
            ? {
                  id: null,
                  url: `/${activeMarket.slug}/${searchSlug}?q=*`,
                  templatePath: '/search?q=*',
              }
            : {
                  id: null,
                  url: `/${activeMarket.slug}/${brandsSlug}`,
                  templatePath: '/brands',
              };

    const mappedCategories = mapCategoriesForMobileMenu([
        ...(categories ?? []),
        {
            ...brandsLinkProps,
            title: t('Brands'),
            categories: brands,
        },
        {
            id: null,
            title: t('Original content'),
            url: `/${activeMarket.slug}/${t('content')}`,
            templatePath: '/content',
            categories: originalContentLinks.map(({ urlSlug, name }) => {
                const contentSlug = t('content', { ns: 'urls' });

                return {
                    id: null,
                    title: name,
                    url: `/${activeMarket.slug}/${contentSlug}/${urlSlug}`,
                    templatePath: `/content/category?slug=${urlSlug}`,
                };
            }),
        },
    ]);

    function handleAccountClick() {
        if (isLoggedIn) {
            router.push('/account', `/${activeMarket.slug}/${t('account')}`);
            return;
        }

        setMenuVisible(false);
        setModalSize(ModalSize.SMALL);
        setModalContent(
            <LoginForm onLogInSuccess={() => setModalVisible(false)} />
        );
        setModalVisible(true);
    }

    useEffect(() => {
        (async () => {
            try {
                const navLinks = await getNavOriginalContent(
                    normalizeIsoCode(activeMarket.iso)
                );
                setOriginalContentLinks(navLinks);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    return (
        <StyledMobileMainNav {...otherProps}>
            <CategoryList
                category={mappedCategories}
                data-testid="mobile-main-nav__categories-list"
                className="categories-list"
            />
            <div className="pre-footer">
                <div
                    className="pre-footer-account"
                    onClick={handleAccountClick}
                >
                    <Icon
                        className="pre-footer-account-icon"
                        variant={IconVariant.DECORATIVE}
                        size={IconSize.M}
                    >
                        <AccountIcon />
                    </Icon>

                    <span className="pre-footer-account-text">
                        {t('My account')}
                    </span>
                </div>
                <WrappedLink
                    prefetch={false}
                    href="/sell-form"
                    data-testid="mobile-main-nav__sell-trade-button"
                    as={`/${activeMarket.slug}/${t('sell-or-trade', {
                        ns: 'urls',
                    })}`}
                >
                    {t('Start selling/trading')} &gt;
                </WrappedLink>
            </div>
            <div className="footer">
                <SocialChannels
                    className="social-channels"
                    data-testid="mobile-main-nav__social-channels"
                />
                <Footer
                    variant={FooterVariant.MOBILE}
                    linkLists={footerLinks}
                    data-testid="mobile-main-nav__footer"
                />
            </div>
        </StyledMobileMainNav>
    );
};
